import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import AsyncStorage from "@react-native-async-storage/async-storage";
import React from "react";

const OFFLINE_CACHE_KEY = "ferina_offline_cache";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 h

/** Query-Keys bzw. Key-Prefixe, die für Offline-Bundle persistiert werden. */
const OFFLINE_BUNDLE_PREFIXES: readonly (string | readonly string[])[] = [
  "entityFields",
  "species",
  ["locations", "assignable"],
  ["locations", "isInternal"],
  ["locations", "unitsForPicker"],
  ["animals", "tenant"],
  ["locations", "my"],
];

function keyMatchesPrefix(
  queryKey: readonly unknown[],
  prefix: string | readonly string[]
): boolean {
  const arr = Array.isArray(prefix) ? [...prefix] : [prefix];
  for (let i = 0; i < arr.length; i++) {
    if (queryKey[i] !== arr[i]) return false;
  }
  return true;
}

function shouldPersistQuery(query: { queryKey: readonly unknown[] }): boolean {
  return OFFLINE_BUNDLE_PREFIXES.some((prefix) =>
    keyMatchesPrefix(query.queryKey, prefix)
  );
}

const asyncStoragePersister = createAsyncStoragePersister({
  storage: AsyncStorage,
  key: OFFLINE_CACHE_KEY,
  throttleTime: 2000,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 15 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

export const queryClientInstance = queryClient;

const persistOptions = {
  persister: asyncStoragePersister,
  maxAge: MAX_AGE_MS,
  dehydrateOptions: {
    shouldDehydrateQuery: shouldPersistQuery,
  },
} as const;

export function QueryProvider({ children }: { children: React.ReactNode }) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
    >
      {children}
    </PersistQueryClientProvider>
  );
}
